<template>
  <el-row
    class="m-0 width-100-p height-100-p"
    v-loading="loading"
    element-loading-background="rgba(122, 122, 122, 0.8)"
  >
    <el-col v-if="statusMessage" :span="24" class="mt-20 px-2">
      <p class="font-14 mb-0" :class="errorMessage ? 'text-danger' : ''">{{ statusMessage }}</p>
    </el-col>

    <el-col v-if="!isAuditor" :class="cardColClass(0)" :span="cardSpan">
      <el-tooltip
        effect="light"
        placement="top"
        :show-after="3000"
      >
        <template #content>
          <div :style="tooltipStyle">
            <div
              v-for="item in startOfMpComponents"
              :key="item.name"
              class="d-flex justify-content-between mb-1"
            >
              <span>{{ item.name }}</span>
              <span class="ml-3">{{ formatNumber(item.value) }}</span>
            </div>
            <div :style="tooltipFormulaStyle">
              {{ startOfMpBreakdown }} = {{ formatNumber(counts.operational_start_of_mp) }}
            </div>
          </div>
        </template>
        <div class="row m-0 py-3 width-100-p height-100-p bg-me-primary border-color-light rounded-4 d-flex align-items-center">
          <div class="d-flex justify-content-between align-items-center width-100-p px-2">
            <div class="flex-grow-1 ml-30">
              <p class="font-25-b text-white mb-0">
                {{ formatNumber(counts.operational_start_of_mp) }}
              </p>
              <p class="font-14 mt-2 text-white mb-0">
                {{ compactTitles ? "Op. chargers before MP" : "Operational chargers before selected MP" }}
              </p>
            </div>
            <div v-if="showLargeIcon" class="width-80 height-80 mr-20 ml-3 flex-shrink-0">
              <svg>
                <use href="@/assets/svg/newImports.svg#operational-chargers-80"></use>
              </svg>
            </div>
            <div v-if="showSmallIcon" class="width-60 height-60 ml-20 mr-20 ml-3 flex-shrink-0">
              <svg>
                <use href="@/assets/svg/newImports.svg#operational-chargers-60"></use>
              </svg>
            </div>
          </div>
        </div>
      </el-tooltip>
    </el-col>

    <el-col :class="cardColClass(isAuditor ? 0 : 1)" :span="cardSpan">
      <el-tooltip
        effect="light"
        placement="top"
        :show-after="3000"
      >
        <template #content>
          <div :style="tooltipStyle">
            <div
              v-for="item in chargersInMpComponents"
              :key="item.name"
              class="d-flex justify-content-between mb-1"
            >
              <span>{{ item.name }}</span>
              <span class="ml-3">{{ formatNumber(item.value) }}</span>
            </div>
            <div :style="tooltipFormulaStyle">
              {{ chargersInMpBreakdown }} = {{ formatNumber(counts.operational_chargers_in_mp) }}
            </div>
          </div>
        </template>
        <div class="row m-0 py-3 width-100-p height-100-p bg-me-primary border-color-light rounded-4 d-flex align-items-center">
          <div class="d-flex justify-content-between align-items-center width-100-p px-2">
            <div class="flex-grow-1 ml-30">
              <p class="font-25-b text-white mb-0">
                {{ formatNumber(counts.operational_chargers_in_mp) }}
              </p>
              <p class="font-14 mt-2 text-white mb-0">
                {{ compactTitles ? "Op. chargers in MP" : "Operational chargers in selected MP" }}
              </p>
            </div>
            <div v-if="showLargeIcon" class="width-80 height-80 mr-20 ml-3 flex-shrink-0">
              <svg>
                <use href="@/assets/svg/newImports.svg#supported-chargers-80"></use>
              </svg>
            </div>
            <div v-if="showSmallIcon" class="width-60 height-60 ml-20 mr-20 ml-3 flex-shrink-0">
              <svg>
                <use href="@/assets/svg/newImports.svg#supported-chargers-60"></use>
              </svg>
            </div>
          </div>
        </div>
      </el-tooltip>
    </el-col>

    <el-col v-if="isAuditor" :class="cardColClass(1)" :span="cardSpan">
      <div class="row m-0 py-3 width-100-p height-100-p bg-me-primary border-color-light rounded-4 d-flex align-items-center">
        <div class="d-flex justify-content-between align-items-center width-100-p px-2">
          <div class="flex-grow-1 ml-30">
            <p class="font-25-b text-white mb-0">
              {{ formatNumber(counts.operational_added_in_mp) }}
            </p>
            <p class="font-14 mt-2 text-white mb-0">
              {{ compactTitles ? "Added in MP" : "Chargers added in selected MP" }}
            </p>
          </div>
          <div v-if="showLargeIcon" class="width-80 height-80 mr-20 ml-3 flex-shrink-0">
            <svg>
              <use href="@/assets/svg/newImports.svg#operational-chargers-80"></use>
            </svg>
          </div>
          <div v-if="showSmallIcon" class="width-60 height-60 ml-20 mr-20 ml-3 flex-shrink-0">
            <svg>
              <use href="@/assets/svg/newImports.svg#operational-chargers-60"></use>
            </svg>
          </div>
        </div>
      </div>
    </el-col>

    <el-col :class="cardColClass(2)" :span="cardSpan">
      <el-tooltip
        effect="light"
        placement="top"
        :show-after="3000"
      >
        <template #content>
          <div :style="tooltipStyle">
            <div
              v-for="item in decommissionedComponents"
              :key="item.name"
              class="d-flex justify-content-between mb-1"
            >
              <span>{{ item.name }}</span>
              <span class="ml-3">{{ formatNumber(item.value) }}</span>
            </div>
            <div :style="tooltipFormulaStyle">
              {{ decommissionedBreakdown }} = {{ formatNumber(counts.decommissioned_or_replaced_in_mp) }}
            </div>
          </div>
        </template>
        <div class="row m-0 py-3 width-100-p height-100-p bg-me-primary border-color-light rounded-4 d-flex align-items-center">
          <div class="d-flex justify-content-between align-items-center width-100-p px-2">
            <div class="flex-grow-1 ml-30">
              <p class="font-25-b text-white mb-0">
                {{ formatNumber(counts.decommissioned_or_replaced_in_mp) }}
              </p>
              <p class="font-14 mt-2 text-white mb-0">
                {{ compactTitles ? "Decommissioned in MP" : "Chargers decommissioned in selected MP" }}
              </p>
            </div>
            <div v-if="showLargeIcon" class="width-80 height-80 mr-20 ml-3 flex-shrink-0">
              <svg>
                <use href="@/assets/svg/newImports.svg#decommissioned-chargers-80"></use>
              </svg>
            </div>
            <div v-if="showSmallIcon" class="width-60 height-60 ml-20 mr-20 ml-3 flex-shrink-0">
              <svg>
                <use href="@/assets/svg/newImports.svg#decommissioned-chargers-60"></use>
              </svg>
            </div>
          </div>
        </div>
      </el-tooltip>
    </el-col>
  </el-row>
</template>

<script>
export default {
  props: {
    dataProp: {
      type: Object,
      default() {
        return {};
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    emptyMessage: {
      type: String,
      default: "",
    },
  },
  computed: {
    counts() {
      return this.dataProp || {};
    },
    isAuditor() {
      return this.$store.state.auth.user != null && this.$store.state.auth.user.is_auditor;
    },
    statusMessage() {
      return this.errorMessage || this.emptyMessage || "";
    },
    cardSpan() {
      if (this.$vuetify.display.width >= 700) {
        return 8;
      }
      return 24;
    },
    compactTitles() {
      return this.$vuetify.display.width >= 700 && this.$vuetify.display.width < 1500;
    },
    showLargeIcon() {
      return this.$vuetify.display.width <= 700 || this.$vuetify.display.width >= 1500;
    },
    showSmallIcon() {
      return this.$vuetify.display.width > 700 && this.$vuetify.display.width < 1500;
    },
    tooltipStyle() {
      return {
        fontSize: "12px",
        textAlign: "left",
        minWidth: "240px",
        lineHeight: "1.5",
      };
    },
    tooltipFormulaStyle() {
      return {
        marginTop: "6px",
        paddingTop: "6px",
        borderTop: "1px solid #dcdfe6",
        fontWeight: "bold",
      };
    },
    startOfMpComponents() {
      return [
        { name: "Year added before MP", value: this.counts.c1_year_added_before_mp },
        { name: "Decommissioned before MP", value: this.counts.c1_decommissioned_before_mp },
        { name: "Active until before MP", value: this.counts.c1_active_until_before_mp },
      ];
    },
    chargersInMpComponents() {
      return [
        { name: "Operational before MP", value: this.counts.operational_start_of_mp },
        { name: "Added in MP", value: this.counts.operational_added_in_mp },
      ];
    },
    decommissionedComponents() {
      return [
        { name: "Decommissioned in MP", value: this.counts.c3_decommissioned_in_mp },
        { name: "Active until in MP", value: this.counts.c3_active_until_in_mp },
      ];
    },
    startOfMpBreakdown() {
      return `${this.formatNumber(this.counts.c1_year_added_before_mp)} − ${this.formatNumber(this.counts.c1_decommissioned_before_mp)} − ${this.formatNumber(this.counts.c1_active_until_before_mp)}`;
    },
    chargersInMpBreakdown() {
      return `${this.formatNumber(this.counts.operational_start_of_mp)} + ${this.formatNumber(this.counts.operational_added_in_mp)}`;
    },
    decommissionedBreakdown() {
      return `${this.formatNumber(this.counts.c3_decommissioned_in_mp)} + ${this.formatNumber(this.counts.c3_active_until_in_mp)}`;
    },
  },
  methods: {
    formatNumber(value) {
      if (value == null || value === "") {
        return "—";
      }
      const num = Number(value);
      if (!Number.isFinite(num)) {
        return "—";
      }
      return num.toLocaleString();
    },
    cardColClass(index) {
      const width = this.$vuetify.display.width;
      if (width < 700) {
        return ["d-flex", "align-items-center", "px-0", "mt-20"];
      }
      if (width < 1500) {
        return ["d-flex", "align-items-center", "px-10", "mt-20"];
      }
      const paddingByIndex = [["pr-12"], ["px-2"], ["pl-20", "pr-8"]];
      return ["d-flex", "align-items-center", "mt-20", ...(paddingByIndex[index] || ["px-2"])];
    },
  },
};
</script>
